import type { Hit, LiteClient } from "algoliasearch/lite";
import type { Indexable } from "../../../core/schema/Indexable.js";

/** A document as it comes back from a query: the indexed fields plus Algolia's highlighting. */
export type AlgoliaHit = Hit<Indexable>;

export interface SearchPage {
	hits: AlgoliaHit[];
	nbPages: number;
}

export interface AlgoliaSearcher {
	searchPage(query: string, page: number): Promise<SearchPage>;
}

/**
 * Algolia's highlighted `name`, with its `<em>` markers turned into `<strong>` for styling.
 * The values are already HTML-escaped by Algolia before it adds the tags.
 */
export function getHighlightedName(hit: AlgoliaHit): string {
	const highlighted = hit._highlightResult?.name;
	const value =
		highlighted !== undefined && !Array.isArray(highlighted) && typeof highlighted.value === "string"
			? highlighted.value
			: hit.name;

	return value.replace(/<em>/gu, "<strong>").replace(/<\/em>/gu, "</strong>");
}

/**
 * Queries one locale's index.
 *
 * Uses the search-only key and the lite client, which carries the search endpoint alone — imported
 * on the first query so it stays out of the initial page bundle.  The write key lives in `indexer/`
 * and never reaches the browser.
 */
export function createAlgoliaSearcher(
	appId: string,
	searchKey: string,
	indexName: string,
	hitsPerPage: number,
): AlgoliaSearcher {
	let clientPromise: Promise<LiteClient> | undefined;

	async function loadClient(): Promise<LiteClient> {
		const { liteClient } = await import("algoliasearch/lite");
		return liteClient(appId, searchKey);
	}

	function getClient(): Promise<LiteClient> {
		clientPromise ??= loadClient();
		return clientPromise;
	}

	return {
		async searchPage(query: string, page: number): Promise<SearchPage> {
			const client = await getClient();
			const { results } = await client.searchForHits<Indexable>({
				requests: [{ indexName, query, hitsPerPage, page }],
			});
			const [result] = results;

			return { hits: result?.hits ?? [], nbPages: result?.nbPages ?? 0 };
		},
	};
}
